import { BackflowJobGroup, BackflowJobItem } from '../model';

const GROUP_KEY_PREFIX = 'backflowJobGroup_';
const ITEM_KEY_PREFIX = 'backflowJobItem_';

const buildGroupKey = (jobGroupId: string) => `${GROUP_KEY_PREFIX}${jobGroupId}`;
const buildItemKey = (itemId: any) => `${ITEM_KEY_PREFIX}${itemId}`;

const getCachedGroup = (jobGroupId: string): any | null => {
  try {
    const data = localStorage.getItem(buildGroupKey(jobGroupId));
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const setCachedGroup = (jobGroupId: string, data: any): void => {
  localStorage.setItem(buildGroupKey(jobGroupId), JSON.stringify(data));
};

// Returns all item entries from a cached group as { id, data } pairs
const getItemEntries = (cached: any): Array<{ id: string; data: any }> =>
  Object.keys(cached)
    .filter((key) => key.startsWith(ITEM_KEY_PREFIX))
    .map((key) => ({ id: key.replace(ITEM_KEY_PREFIX, ''), data: cached[key] }));

const checkAndCleanup = (jobGroupId: string, cached: any): void => {
  const allSynced = getItemEntries(cached).every(({ data }) => {
    const deviceSynced = !data.backflow_job_item?.needsSync;
    const logSynced = !data.backflow_log || !data.backflow_log.needsSync;
    return deviceSynced && logSynced;
  });

  if (allSynced) {
    localStorage.removeItem(buildGroupKey(jobGroupId));
  } else {
    setCachedGroup(jobGroupId, cached);
  }
};

// Saves the full job group and its items to the cache.
// Fresh server data is used for device and log fields unless they have unsynced offline changes.
export const saveJobGroupToCache = (
  jobGroupId: string,
  jobGroup: BackflowJobGroup,
  jobItems: BackflowJobItem[],
): void => {
  try {
    const existing = getCachedGroup(jobGroupId);

    const updated: any = {
      cachedAt: new Date().toISOString(),
      jobGroup,
    };

    jobItems.forEach((freshItem) => {
      const existingItemData = existing?.[buildItemKey(freshItem.id)];

      updated[buildItemKey(freshItem.id)] = {
        backflow_job_item: existingItemData?.backflow_job_item?.needsSync
          ? existingItemData.backflow_job_item
          : {
              asset_no: freshItem.asset_no,
              backflow_type: freshItem.backflow_type,
              bfp_manufacturer_id: freshItem.bfp_manufacturer_id,
              location: freshItem.location,
              model_no: freshItem.model_no,
              serial_no: freshItem.serial_no,
              status: freshItem.status,
              water_meter_serial: freshItem.water_meter_serial,
              water_meter_serial_secondary: freshItem.water_meter_serial_secondary,
              wm_manufacturer_id: freshItem.wm_manufacturer_id,
              needsSync: false,
            },
        backflow_log: existingItemData?.backflow_log?.needsSync ? existingItemData.backflow_log : null,
      };
    });

    setCachedGroup(jobGroupId, updated);
  } catch (error) {
    console.error('Failed to save job group to cache:', error);
  }
};

// Loads a cached job group, returning flat job items suitable for display.
// Device fields are spread directly onto each item; backflow_log is preserved as a nested object.
export const loadJobGroupFromCache = (
  jobGroupId: string,
): { jobGroup: BackflowJobGroup; jobItems: BackflowJobItem[] } | null => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return null;

    const jobItems = getItemEntries(cached).map(({ id, data }) => ({
      id,
      backflow_job_group_id: cached.jobGroup?.id,
      ...data.backflow_job_item,
      backflow_log: data.backflow_log,
    }));

    return { jobGroup: cached.jobGroup, jobItems };
  } catch (error) {
    console.error('Failed to load job group from cache:', error);
    return null;
  }
};

// Searches all cached job groups for a specific item by ID.
export const findJobItemInCache = (itemId: string): BackflowJobItem | null => {
  try {
    const groupKeys = Object.keys(localStorage).filter((key) => key.startsWith(GROUP_KEY_PREFIX));

    for (const key of groupKeys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const cached = JSON.parse(raw);
      const itemData = cached[buildItemKey(itemId)];

      if (itemData) {
        return {
          id: itemId,
          backflow_job_group_id: cached.jobGroup?.id,
          ...itemData.backflow_job_item,
          backflow_log: itemData.backflow_log,
        } as BackflowJobItem;
      }
    }

    return null;
  } catch (error) {
    console.error('Failed to find job item in cache:', error);
    return null;
  }
};

// Returns true if the job group is still present in the cache.
export const isJobGroupCached = (jobGroupId: string): boolean =>
  localStorage.getItem(buildGroupKey(jobGroupId)) !== null;

// Loads the saved log for a specific item, splitting out metadata from form values.
export const loadCachedLog = (jobGroupId: string, itemId: any): { formValues: any; outOfService: boolean } | null => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return null;

    const log = cached[buildItemKey(itemId)]?.backflow_log;
    if (!log) return null;

    const { savedAt, needsSync, outOfService, ...formValues } = log;
    return { formValues, outOfService: outOfService || false };
  } catch (error) {
    console.error('Failed to load cached log:', error);
    return null;
  }
};

// Writes the full log state to the cache with needsSync: true.
// Returns false if the job group or item is not found in the cache.
export const persistLog = (jobGroupId: string, itemId: any, formValues: any, outOfService: boolean): boolean => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return false;

    const key = buildItemKey(itemId);
    if (!cached[key]) return false;

    cached[key].backflow_log = {
      ...formValues,
      outOfService,
      savedAt: new Date().toISOString(),
      needsSync: true,
    };

    setCachedGroup(jobGroupId, cached);
    return true;
  } catch (error) {
    console.error('Failed to persist log:', error);
    return false;
  }
};

// Merges partial form values into the existing cached log with needsSync: true.
// Used for autosave, where outOfService and other metadata are preserved from the prior save.
export const autosaveLog = (jobGroupId: string, itemId: any, partialValues: any): void => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return;

    const key = buildItemKey(itemId);
    if (!cached[key]) return;

    const existingLog = cached[key].backflow_log;
    cached[key].backflow_log = {
      ...existingLog,
      ...partialValues,
      savedAt: new Date().toISOString(),
      needsSync: true,
    };

    setCachedGroup(jobGroupId, cached);
  } catch (error) {
    console.error('Failed to autosave log:', error);
  }
};

// Marks a log as synced and removes the entire job group from the cache if all items are synced.
export const markLogSynced = (jobGroupId: string, itemId: any): void => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return;

    const key = buildItemKey(itemId);
    if (!cached[key]?.backflow_log) return;

    cached[key].backflow_log.needsSync = false;
    checkAndCleanup(jobGroupId, cached);
  } catch (error) {
    console.error('Failed to mark log as synced:', error);
  }
};

// Marks a device as synced and removes the entire job group from the cache if all items are synced.
export const markDeviceSynced = (jobGroupId: string, itemId: any): void => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return;

    const key = buildItemKey(itemId);
    if (!cached[key]?.backflow_job_item) return;

    cached[key].backflow_job_item.needsSync = false;
    checkAndCleanup(jobGroupId, cached);
  } catch (error) {
    console.error('Failed to mark device as synced:', error);
  }
};

// Merges a partial patch into the existing cached device data.
// Use this when only a few fields need updating (e.g. status, emp_id) without replacing the full record.
// Returns false if the job group, item, or existing device record is not found in the cache.
export const patchDevice = (
  jobGroupId: string,
  itemId: any,
  patch: Record<string, any>,
  needsSync: boolean,
): boolean => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return false;

    const key = buildItemKey(itemId);
    if (!cached[key]?.backflow_job_item) return false;

    cached[key].backflow_job_item = {
      ...cached[key].backflow_job_item,
      ...patch,
      needsSync,
      savedAt: new Date().toISOString(),
    };

    setCachedGroup(jobGroupId, cached);
    return true;
  } catch (error) {
    console.error('Failed to patch device:', error);
    return false;
  }
};

// Writes device data to the cache with the given needsSync value.
// Returns false if the job group or item is not found in the cache.
export const persistDevice = (
  jobGroupId: string,
  itemId: any,
  formValues: BackflowJobItem,
  needsSync: boolean,
): boolean => {
  try {
    const cached = getCachedGroup(jobGroupId);
    if (!cached) return false;

    const key = buildItemKey(itemId);
    if (!cached[key]) return false;

    cached[key].backflow_job_item = {
      asset_no: formValues.asset_no,
      backflow_type: formValues.backflow_type,
      bfp_manufacturer_id: formValues.bfp_manufacturer_id,
      location: formValues.location,
      model_no: formValues.model_no,
      serial_no: formValues.serial_no,
      status: formValues.status,
      water_meter_serial: formValues.water_meter_serial,
      water_meter_serial_secondary: formValues.water_meter_serial_secondary,
      wm_manufacturer_id: formValues.wm_manufacturer_id,
      needsSync,
      savedAt: new Date().toISOString(),
    };

    setCachedGroup(jobGroupId, cached);
    return true;
  } catch (error) {
    console.error('Failed to persist device:', error);
    return false;
  }
};
